import { supabase } from './supabase';

/**
 * Envío del correo para restablecer la contraseña.
 *
 * Lo hace Supabase (GoTrue), que ya tiene el SMTP configurado. No hay que
 * construir el envío, solo pedirlo bien.
 *
 * LA REGLA QUE NO SE ROMPE: al usuario anónimo se le contesta SIEMPRE lo
 * mismo, exista o no el correo. Decirle «ese correo no está registrado»
 * convierte el formulario en una herramienta para averiguar qué direcciones
 * tienen cuenta, probando una por una. GoTrue ya devuelve 200 en ambos casos
 * —comprobado contra producción—; esto solo se encarga de no delatarlo en la
 * pantalla.
 *
 * El patrón viene de CalzadoPro, donde ya está en producción.
 */

/** A dónde vuelve el usuario al pulsar el enlace del correo. */
function urlDeRegreso(): string {
  // El mismo origen desde el que se pidió: en producción grooming-erp.com, en
  // desarrollo localhost. Así no hay que mantener una lista de dominios.
  // `ADDITIONAL_REDIRECT_URLS` del compose ya incluye el comodín del dominio.
  return `${window.location.origin}/restablecer`;
}

export interface ResultadoEnvio {
  ok: boolean;
  /** Solo cuando se pide revelar el motivo: el fallo real. */
  motivo?: string;
}

/**
 * Pide el correo de recuperación.
 *
 * @param email        a quién
 * @param revelarError si es false —el caso del usuario anónimo— devuelve `ok`
 *                     aunque falle, para no delatar qué correos existen.
 */
export async function enviarRecuperacion(
  email: string,
  revelarError = false,
  captchaToken?: string | null,
): Promise<ResultadoEnvio> {
  const limpio = email.trim().toLowerCase();

  // Validación de forma, no de existencia: atrapa el dedazo sin consultar nada
  // ni delatar nada.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(limpio)) {
    return { ok: false, motivo: 'Ese correo no tiene un formato válido.' };
  }

  // El captcha aplica también aquí: GoTrue lo exige en `/recover`, no solo
  // al iniciar sesión. Sin él, TODO envío moría con `captcha_failed` — y el
  // mensaje neutro de abajo lo tapaba, así que la pantalla decía «te llegó un
  // enlace» sin haber enviado nada. Estuvo así desde el 25/09.
  const { error } = await supabase.auth.resetPasswordForEmail(limpio, {
    redirectTo: urlDeRegreso(),
    ...(captchaToken ? { captchaToken } : {}),
  });

  if (error) {
    // Al usuario anónimo NO se le cuenta qué pasó: un «usuario no encontrado»
    // y un «correo enviado» distinguibles delatan qué direcciones existen.
    // Se registra en consola para poder diagnosticarlo sin exponerlo.
    console.error('[recuperación] falló el envío:', error.message);

    // El silencio protege la privacidad, no los fallos del sistema.
    //
    // Callar un «ese correo no existe» es correcto: distinguirlo delataría
    // qué cuentas hay. Callar un captcha caído o el servidor sin responder
    // es mentirle al usuario: se queda esperando un correo que nunca salió.
    // Se distingue por el TIPO de fallo, no por quién pregunta.
    const fallaElSistema = /captcha|turnstile|network|failed to fetch|5\d\d/i
      .test(error.message);
    if (fallaElSistema) {
      return {
        ok: false,
        motivo: 'No pudimos enviar el correo ahora mismo. Recarga la página e intenta de nuevo.',
      };
    }

    if (!revelarError) return { ok: true };

    const limite = /rate|too many|limit/i.test(error.message);
    return {
      ok: false,
      motivo: limite
        ? 'Demasiados intentos seguidos. Espera un minuto y vuelve a probar.'
        : error.message,
    };
  }

  return { ok: true };
}

/**
 * El mensaje que ve quien NO está autenticado.
 *
 * Deliberadamente idéntico exista o no el correo. Está aquí, en una constante,
 * para que nadie lo cambie por descuido en una de las dos ramas y acabe
 * revelando la diferencia.
 */
export const MENSAJE_NEUTRO =
  'Si ese correo está registrado, te enviamos un enlace para restablecer la '
  + 'contraseña. Revisa también la carpeta de correo no deseado.';

/** Cuánto dura el enlace. Lo fija GoTrue; se dice para no dejar esperando. */
export const DURACION_ENLACE = 'El enlace caduca en una hora.';
